/**
 * DIE ACHSEN EINES BERICHTS (AUS-16, Auftrag CRM-42) — eine Regel fuer Maske
 * und Server.
 *
 * OHNE EINFUHR, wie `raster.ts`: Die Oberflaeche fuehrt diese Datei ueber
 * ihren Quellpfad ein (`@crm/kontrakte/src/achsen`), die API ueber das Paket.
 * Liefen die beiden auseinander, boete die Maske eine Kennzahl an, die der
 * Server danach mit 400 abweist.
 *
 * ============================================================================
 * DIE REGEL
 * ============================================================================
 *
 * Eine Achse traegt EINE Einheit. Die Einheit der ersten Kennzahl bestimmt
 * die linke Achse; Kennzahlen derselben Einheit kommen dazu, bis zu fuenf.
 * Die erste Kennzahl mit einer anderen Einheit legt die rechte Achse an, auch
 * dort bis zu fuenf derselben Einheit. Eine dritte Einheit hat keinen Platz.
 *
 * Die Achse folgt aus der Liste und wird nicht gespeichert: Faellt die letzte
 * Kennzahl der linken Achse weg, ist die erste verbliebene die neue erste —
 * die rechte Achse rueckt so von selbst nach links.
 *
 * Die Regel kennt die Einheiten nicht beim Namen. Sie vergleicht nur, ob zwei
 * gleich sind; eine neue Einheit (etwa Stunden) passt ohne Umbau.
 */

export const HOECHSTENS_ACHSEN = 2;
export const HOECHSTENS_JE_ACHSE = 5;
/** So viele Kennzahlen traegt eine Beschreibung hoechstens (Fassung 3). */
export const HOECHSTENS_KENNZAHLEN = HOECHSTENS_ACHSEN * HOECHSTENS_JE_ACHSE;

export type Achsenseite = 'links' | 'rechts';

type MitEinheit = { readonly einheit: string };

/** Die Einheiten der Liste in der Folge ihres ersten Auftretens — die erste ist die linke Achse. */
export function einheitenVon(ks: readonly MitEinheit[]): readonly string[] {
  return [...new Set(ks.map((k) => k.einheit))];
}

/** Auf welcher Achse die Kennzahl an Stelle `i` steht. */
export function achseVon(ks: readonly MitEinheit[], i: number): Achsenseite {
  const erste = ks[0];
  const k = ks[i];
  return erste === undefined || k === undefined || k.einheit === erste.einheit ? 'links' : 'rechts';
}

/**
 * Warum `neu` nicht mehr neben `gewaehlt` passt — oder null. Nur die
 * Achsenregel; ob Gruppierung und Filter passen, prueft der Aufrufer.
 */
export function achsengrund(gewaehlt: readonly MitEinheit[], neu: MitEinheit): string | null {
  const einheiten = einheitenVon(gewaehlt);
  if (einheiten.includes(neu.einheit)) {
    const belegt = gewaehlt.filter((k) => k.einheit === neu.einheit).length;
    return belegt >= HOECHSTENS_JE_ACHSE
      ? `Auf der Achse für ${neu.einheit} stehen schon ${String(HOECHSTENS_JE_ACHSE)} Kennzahlen.`
      : null;
  }
  return einheiten.length >= HOECHSTENS_ACHSEN
    ? `Beide Achsen sind belegt (${einheiten.join(' und ')}); für ${neu.einheit} gäbe es eine dritte Achse.`
    : null;
}

/** Alle Maengel einer ganzen Liste — fuer die Pruefung des Servers und des KI-Vorschlags. */
export function achsenmaengel(ks: readonly MitEinheit[]): readonly string[] {
  const einheiten = einheitenVon(ks);
  const maengel: string[] = [];
  if (einheiten.length > HOECHSTENS_ACHSEN) {
    maengel.push(
      `Die Kennzahlen haben ${String(einheiten.length)} Einheiten (${einheiten.join(', ')}); zwei Achsen tragen höchstens zwei.`,
    );
  }
  for (const einheit of einheiten) {
    const anzahl = ks.filter((k) => k.einheit === einheit).length;
    if (anzahl > HOECHSTENS_JE_ACHSE) {
      maengel.push(
        `${String(anzahl)} Kennzahlen in ${einheit}; eine Achse trägt höchstens ${String(HOECHSTENS_JE_ACHSE)}.`,
      );
    }
  }
  return maengel;
}

/*
 * ============================================================================
 * DIE REIHEN EINER AUFTEILUNG (AUS-16, Auftrag CRM-46)
 * ============================================================================
 *
 * Mit „Aufteilen nach" ist jede Reihe des Bildes eine Kennzahl mal ein Wert
 * der Aufteilung: „Teilnehmer · Privatkunde". Mehr als zehn Reihen liest
 * niemand — die Farben aus CRM-42 sind zehn. Je Kennzahl bekommt das Bild
 * deshalb hoechstens zehn durch die Zahl der Kennzahlen Reihen; gibt es mehr
 * Werte, stehen die groessten fuer sich und der Rest als „Übrige". Damit je
 * Kennzahl wenigstens ein Wert und „Übrige" Platz haben, traegt ein
 * aufgeteilter Bericht hoechstens fuenf Kennzahlen. Die Tabelle zeigt immer
 * alle Werte.
 */

export const HOECHSTENS_REIHEN = 10;
export const HOECHSTENS_KENNZAHLEN_AUFGETEILT = 5;

/**
 * Wie viele Werte der Aufteilung das Bild je Kennzahl als eigene Reihe zeigt.
 * Ist die Antwort kleiner als `werte`, kommt „Übrige" als weitere Reihe dazu.
 */
export function werteImBild(kennzahlen: number, werte: number): number {
  const je = Math.max(2, Math.floor(HOECHSTENS_REIHEN / Math.max(1, kennzahlen)));
  return werte <= je ? werte : je - 1;
}
